/**
 * 🤖 AI Asistan Servisi — gts_ai_api istemcisi.
 *
 * AI chat (sohbet + ajan modu) ve AI rapor önerisi fonksiyonları buradadır.
 * Bu fonksiyonlar eskiden salesService içindeydi; tüm Gemini çağrıları
 * gts_ai_api'ye taşınınca buraya alındı (Temmuz 2026).
 *
 * NOT: Dinamik raporun ÇALIŞTIRILMASI (runDynamicReport / getDynamicReportCatalog)
 * satış verisi okuduğu için salesService'te (gts_sales_api) kalmıştır — AI yalnızca
 * rapor tarifini (ReportSpec) üretir, tarif yine sales api'de çalıştırılır.
 */
import { aiApi } from '../config/apiClient';

// ============================================
// AI CHAT ASSISTANT TYPES
// ============================================

/** AI chat durumu: FAB görünürlüğü + kalan kota */
export interface AiChatStatus {
  /** Sunucuda API anahtarı var mı? True ise FAB tüm şirketlerde gösterilir. */
  configured: boolean;
  /** Şirketin AI paketi aktif mi? False ise gönderim paket tanıtımına yönlendirir. */
  enabled: boolean;
  /** AI Pro: ajan modu izni (SuperAdmin yönetir). */
  agentEnabled: boolean;
  /** AI Pro: plan modu izni (SuperAdmin yönetir). */
  planEnabled: boolean;
  monthlyTokenLimit: number;
  usedTokens: number;
  remainingTokens: number;
}

/** AI kullanım istatistikleri — günlük kırılım satırı */
export interface AiChatDailyUsage {
  /** Türkiye günü, 'yyyy-MM-dd' */
  date: string;
  totalTokens: number;
  promptTokens: number;
  outputTokens: number;
  requestCount: number;
}

/** AI kullanım istatistikleri — aylık trend satırı */
export interface AiChatMonthlyUsage {
  year: number;
  month: number;
  totalTokens: number;
  promptTokens: number;
  outputTokens: number;
  requestCount: number;
}

/** AI kullanım istatistik sayfası verisi (GET /api/AiChat/usage) */
export interface AiChatUsage {
  year: number;
  month: number;
  monthlyTokenLimit: number;
  usedTokens: number;
  remainingTokens: number;
  promptTokens: number;
  outputTokens: number;
  requestCount: number;
  daily: AiChatDailyUsage[];
  /** Son 6 ay (eski → yeni) */
  months: AiChatMonthlyUsage[];
}

export interface AiChatConversationSummary {
  id: number;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

/** Assistant mesajına gömülü, backend whitelist'inden geçmiş rapor tarifi */
export interface AiChatReportSpecDto {
  dataSource: string;
  metric: string;
  dimension: string;
  timeGranularity?: string;
  filters: Array<{ field: string; values: string[] }>;
  visual: string;
  limit: number;
  title: string;
  /** AI'nın kullanıcı isteğinden çözdüğü tarih aralığı ("yyyy-MM-dd", dahil). Boşsa varsayılan aralık kullanılır. */
  startDate?: string;
  endDate?: string;
}

export interface AiChatMessageDto {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  totalTokens: number;
  createdAt: string;
  reportSpec: AiChatReportSpecDto | null;
  /** Ajan part türü: undefined/'text' | 'functionCall' | 'functionResponse' (functionResponse satırları gizlenir) */
  kind?: string | null;
  /** kind='functionCall' ise eylem adı (geçmişte "eylem çalıştırıldı" göstergesi) */
  actionName?: string | null;
}

/** Ajan modunda AI'nın çağırmak istediği, backend katalog doğrulamasından geçmiş eylem */
export interface AiChatActionCallDto {
  /** Kalıcı functionCall mesaj id'si */
  messageId: number;
  name: string;
  /** Gemini argümanları (parse edilmiş nesne) */
  args: Record<string, any>;
  /** 'read' → onaysız çalışır | 'write' → onay kartı gerekir */
  risk: 'read' | 'write';
}

export interface AiChatSendResult {
  success: boolean;
  /** AI_DISABLED | AGENT_DISABLED | QUOTA_EXCEEDED | RATE_LIMIT (Gemini 429) | AI_BUSY (Gemini 5xx) | AI_ERROR */
  code: string | null;
  reply: string;
  reportSpec: AiChatReportSpecDto | null;
  conversationId: number;
  remainingTokens: number;
  /** Ajan modu: model bir eylem çağırmak istiyorsa dolu (reply boş olabilir) */
  actionCall: AiChatActionCallDto | null;
}

/** AI asistanın önerdiği rapor tarifi (id/başlık frontend'te tamamlanır) */
export interface AiReportSuggestion {
  spec: {
    dataSource: string;
    metric: string;
    dimension: string;
    timeGranularity?: string;
    filters: Array<{ field: string; values: string[] }>;
    visual: string;
    limit: number;
    title: string;
  };
  note: string | null;
}

// ============================================
// MAPPERS
// ============================================

const mapAiChatReportSpec = (s: any): AiChatReportSpecDto => ({
  dataSource: s.DataSource ?? '',
  metric: s.Metric ?? '',
  dimension: s.Dimension ?? 'none',
  timeGranularity: s.TimeGranularity ?? undefined,
  filters: (s.Filters || []).map((f: any) => ({
    field: f.Field ?? '',
    values: f.Values ?? [],
  })),
  visual: s.Visual ?? 'bar',
  limit: s.Limit ?? 10,
  title: s.Title ?? '',
  startDate: s.StartDate ?? undefined,
  endDate: s.EndDate ?? undefined,
});

const mapAiChatMessage = (m: any): AiChatMessageDto => ({
  id: m.Id ?? 0,
  role: m.Role === 'assistant' ? 'assistant' : 'user',
  content: m.Content ?? '',
  totalTokens: m.TotalTokens ?? 0,
  createdAt: m.CreatedAt ?? '',
  reportSpec: m.ReportSpec ? mapAiChatReportSpec(m.ReportSpec) : null,
  kind: m.Kind ?? null,
  actionName: m.ActionName ?? null,
});

const mapAiChatActionCall = (a: any): AiChatActionCallDto | null => {
  if (!a?.Name) return null;
  let args: Record<string, any> = {};
  try {
    const parsed = JSON.parse(a.ArgsJson || '{}');
    if (parsed && typeof parsed === 'object') args = parsed;
  } catch {
    /* bozuk args — boş nesneyle devam */
  }
  return {
    messageId: a.MessageId ?? 0,
    name: a.Name,
    args,
    risk: a.Risk === 'write' ? 'write' : 'read',
  };
};

const mapAiChatSendResult = (d: any): AiChatSendResult => ({
  success: d?.Success === true,
  code: d?.Code ?? null,
  reply: d?.Reply ?? '',
  reportSpec: d?.ReportSpec ? mapAiChatReportSpec(d.ReportSpec) : null,
  conversationId: d?.ConversationId ?? 0,
  remainingTokens: Number(d?.RemainingTokens) || 0,
  actionCall: d?.ActionCall ? mapAiChatActionCall(d.ActionCall) : null,
});

// ============================================
// SERVICE
// ============================================

class AiChatService {
  /**
   * 🤖 AI chat durumu: şirket bayrağı (SuperAdmin) + sunucu yapılandırması + kalan kota.
   * enabled=false ise FAB hiç render edilmez.
   */
  async getAiChatStatus(): Promise<AiChatStatus> {
    const response = await aiApi.get<any>('/api/AiChat/status');
    const d = response.data;
    return {
      // Eski backend Configured alanını dönmez — o durumda eski davranış (enabled = görünür) korunur
      configured: d?.Configured === true || d?.Enabled === true,
      enabled: d?.Enabled === true,
      agentEnabled: d?.AgentEnabled === true,
      planEnabled: d?.PlanEnabled === true,
      monthlyTokenLimit: Number(d?.MonthlyTokenLimit) || 0,
      usedTokens: Number(d?.UsedTokens) || 0,
      remainingTokens: Number(d?.RemainingTokens) || 0,
    };
  }

  /**
   * 🤖 AI kullanım istatistikleri: seçilen ayın özeti + günlük kırılım + son 6 ay trendi.
   * 3 moddan hiçbiri açık değilse backend 403 döner (çağıran taraf yakalar).
   */
  async getAiChatUsage(year?: number, month?: number): Promise<AiChatUsage> {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (month) params.set('month', String(month));
    const qs = params.toString();
    const response = await aiApi.get<any>(`/api/AiChat/usage${qs ? `?${qs}` : ''}`);
    const d = response.data;
    if (d?.Success !== true) throw new Error(d?.Message || 'AI usage failed');
    return {
      year: Number(d.Year) || new Date().getFullYear(),
      month: Number(d.Month) || new Date().getMonth() + 1,
      monthlyTokenLimit: Number(d.MonthlyTokenLimit) || 0,
      usedTokens: Number(d.UsedTokens) || 0,
      remainingTokens: Number(d.RemainingTokens) || 0,
      promptTokens: Number(d.PromptTokens) || 0,
      outputTokens: Number(d.OutputTokens) || 0,
      requestCount: Number(d.RequestCount) || 0,
      daily: (d.Daily || []).map((r: any) => ({
        date: r.Date ?? '',
        totalTokens: Number(r.TotalTokens) || 0,
        promptTokens: Number(r.PromptTokens) || 0,
        outputTokens: Number(r.OutputTokens) || 0,
        requestCount: Number(r.RequestCount) || 0,
      })),
      months: (d.Months || []).map((r: any) => ({
        year: Number(r.Year) || 0,
        month: Number(r.Month) || 0,
        totalTokens: Number(r.TotalTokens) || 0,
        promptTokens: Number(r.PromptTokens) || 0,
        outputTokens: Number(r.OutputTokens) || 0,
        requestCount: Number(r.RequestCount) || 0,
      })),
    };
  }

  /** 🤖 Kullanıcının AI chat konuşma listesi (sunucudan, son 50). */
  async getAiChatConversations(userId: string): Promise<AiChatConversationSummary[]> {
    const response = await aiApi.get<any>(
      `/api/AiChat/conversations?userId=${encodeURIComponent(userId)}`
    );
    const d = response.data;
    if (d?.Success !== true) throw new Error(d?.Message || 'AI chat conversations failed');
    return (d.Data || []).map((c: any) => ({
      id: c.Id ?? 0,
      title: c.Title ?? '',
      createdAt: c.CreatedAt ?? '',
      updatedAt: c.UpdatedAt ?? '',
      messageCount: c.MessageCount ?? 0,
    }));
  }

  /** 🤖 Tek konuşmanın mesajları (rapor tarifleri yeniden ayrıştırılmış halde). */
  async getAiChatConversationMessages(conversationId: number, userId: string): Promise<AiChatMessageDto[]> {
    const response = await aiApi.get<any>(
      `/api/AiChat/conversations/${conversationId}?userId=${encodeURIComponent(userId)}`
    );
    const d = response.data;
    if (d?.Success !== true) throw new Error(d?.Message || 'AI chat conversation failed');
    return (d.Data || []).map(mapAiChatMessage);
  }

  /**
   * 🤖 AI chat mesajı gönder. Kota aşımında code=QUOTA_EXCEEDED döner (Gemini çağrılmaz);
   * token sayımı backend'de Gemini usageMetadata ile yapılır.
   */
  async sendAiChatMessage(params: {
    text: string;
    conversationId?: number;
    userId: string;
    userEmail?: string | null;
    branches?: Array<{ id: string; name: string }>;
    /**
     * 'ask' (varsayılan) | 'agent' | 'plan' — ajan modu AiChatAgentEnabled,
     * plan modu AiChatPlanEnabled (AI Pro) gerektirir.
     */
    mode?: 'ask' | 'agent' | 'plan';
    /** Model anahtarı ('gemini-flash' | 'gemini-3.5' | 'gemini-pro'); boşsa sunucu varsayılanı */
    model?: string;
    /** Durdur butonu: istek beklenmeden iptal edilebilsin diye AbortController sinyali */
    signal?: AbortSignal;
  }): Promise<AiChatSendResult> {
    const response = await aiApi.post<any>(
      '/api/AiChat/message',
      {
        text: params.text,
        conversationId: params.conversationId ?? null,
        userId: params.userId,
        userEmail: params.userEmail ?? null,
        branches: params.branches ?? [],
        mode: params.mode ?? 'ask',
        model: params.model ?? null,
      },
      { signal: params.signal }
    );
    return mapAiChatSendResult(response.data);
  }

  /**
   * 🤖 Ajan modu: frontend'de yürütülen eylemin sonucunu backend'e bildirir;
   * Gemini functionResponse ile devam turu yapar (yeni eylem veya metin dönebilir).
   */
  async sendAiChatActionResult(params: {
    conversationId: number;
    actionName: string;
    /** Yürütme sonucu — JSON'a çevrilebilir nesne (ör. { success: true, ... } / { cancelled: true }) */
    result: unknown;
    userId: string;
    branches?: Array<{ id: string; name: string }>;
    /**
     * Eylem zincirinin başladığı mod ('agent' varsayılan | 'plan') — devam turunda
     * aynı system prompt kullanılsın diye backend'e taşınır.
     */
    mode?: 'ask' | 'agent' | 'plan';
    /** Model anahtarı ('gemini-flash' | 'gemini-3.5' | 'gemini-pro'); boşsa sunucu varsayılanı */
    model?: string;
    /** Durdur butonu: devam turu beklenmeden iptal edilebilsin diye AbortController sinyali */
    signal?: AbortSignal;
  }): Promise<AiChatSendResult> {
    const response = await aiApi.post<any>(
      '/api/AiChat/action-result',
      {
        conversationId: params.conversationId,
        actionName: params.actionName,
        resultJson: JSON.stringify(params.result ?? {}),
        userId: params.userId,
        branches: params.branches ?? [],
        mode: params.mode ?? null,
        model: params.model ?? null,
      },
      { signal: params.signal }
    );
    return mapAiChatSendResult(response.data);
  }

  /**
   * 🤖 AI rapor önerisi aktif mi? Dashboard "AI ile öner" butonunun görünürlüğü için
   * (eskiden sales api DynamicReport/catalog yanıtındaki AiEnabled alanıydı).
   */
  async getAiReportStatus(): Promise<boolean> {
    const response = await aiApi.get<any>('/api/AiReport/status');
    return response.data?.Enabled === true;
  }

  /**
   * 🤖 AI rapor asistanı: doğal dil isteğini ReportSpec önerisine çevirir.
   * LLM çağrısı backend'de yapılır; öneri whitelist doğrulamasından geçmiş olarak döner.
   */
  async suggestDynamicReport(
    text: string,
    branches: Array<{ id: string; name: string }>
  ): Promise<AiReportSuggestion> {
    const response = await aiApi.post<any>('/api/AiReport/suggest', {
      text,
      branches,
    });
    const d = response.data;
    if (d?.Success !== true) {
      const err = new Error(d?.Message || 'AI suggestion failed') as Error & { code?: string };
      err.code = d?.Code;
      throw err;
    }
    const spec = d.Spec || {};
    return {
      spec: {
        dataSource: spec.DataSource ?? '',
        metric: spec.Metric ?? '',
        dimension: spec.Dimension ?? 'none',
        timeGranularity: spec.TimeGranularity ?? undefined,
        filters: (spec.Filters || []).map((f: any) => ({
          field: f.Field ?? '',
          values: f.Values ?? [],
        })),
        visual: spec.Visual ?? 'bar',
        limit: spec.Limit ?? 10,
        title: spec.Title ?? '',
      },
      note: d.Note ?? null,
    };
  }
}

const aiChatService = new AiChatService();
export default aiChatService;
